import { Rng } from '../core/rng';
import { bearingToX, bearingToZ, clamp, wrapAngle, Vec3 } from '../core/math';
import { AMMO, DIFFICULTIES, PERSONALITIES, type AmmoId, type Difficulty, type DifficultyId, type Personality } from '../data/classes';
import { cannonMount } from '../data/ship';
import { TRAIN_LIMIT } from './cannons';
import { awarenessRate, sightRange } from './detection';
import type { Vessel } from './vessel';
import type { Wind } from './wind';
import type { SimEvent, SimEvents } from './events';

// An AI captain. He only knows what his lookouts see and what he hears:
// contacts have to be spotted (weather, darkness, sea state and range all
// matter), are estimated rather than exact, go stale when lost, and can be
// passed on by signal to consorts. From that picture he chooses what to do
// (patrol, investigate, engage, pursue, flee, regroup, board, ram), picks
// a heading by scoring manoeuvres the way a seaman would (the wind rules
// everything), fights his guns with ranging, lead and roll timing, and
// changes ammunition to suit. Difficulty buys better judgement, faster
// reactions and teamwork - not better aim.

export type Mode = 'patrol' | 'investigate' | 'search' | 'engage' | 'pursue' | 'flee' | 'regroup' | 'board' | 'ram' | 'idle';

export interface Contact {
  vessel: number;
  x: number;
  z: number;
  vx: number;
  vz: number;
  heading: number;
  /** World time last actually seen. */
  seen: number;
  /** Detection progress 0..1 (seen when >= 1). */
  awareness: number;
  visible: boolean;
  /** Came from a signal or a sound, not our own lookouts. */
  secondHand: boolean;
}

export interface Battlefield {
  vessels: readonly Vessel[];
  time: number;
  wind: Wind;
  /** Meteorological visibility, m. */
  visibility: number;
  /** 0 night .. 1 day. */
  daylight: number;
  /** Significant wave height, m. */
  seaState: number;
  events: SimEvents;
  /** Events from the previous step (gunfire heard, explosions seen). */
  recent: readonly SimEvent[];
  /** Who is attacking whom (for flanking). */
  attackers: Map<number, number[]>;
}

export function hostile(a: Vessel, b: Vessel): boolean {
  if (a === b || !b.alive) return false;
  if (a.faction === 'merchant' || b.faction === 'merchant') return a.faction === 'pirate' || a.faction === 'player' ? b.faction === 'merchant' : b.faction === 'player' || b.faction === 'pirate';
  if (a.faction === b.faction) return false;
  return true;
}

/** Rough fighting power: guns x weight x men x hull. */
export function power(v: Vessel): number {
  let guns = 0;
  for (const g of v.battery.guns) if (!g.destroyed) guns++;
  return guns * v.battery.weight * (0.3 + 0.7 * v.crew.strength) * (0.3 + 0.7 * v.damage.integrity) + v.crew.effective * 0.05;
}

/** How fast she goes on a given point of sail (fraction of her best). */
function polar(windAngle: number): number {
  const a = Math.abs(windAngle);
  if (a < 0.95) return 0.05;
  if (a < 1.2) return 0.5 + (a - 0.95) * 1.2;
  if (a < 2.2) return 0.85 + (a - 1.2) * 0.15;
  return 1.0 - (a - 2.2) * 0.2;
}

export class Captain {
  readonly personality: Personality;
  readonly difficulty: Difficulty;
  mode: Mode = 'patrol';
  modeTime = 0;
  readonly contacts = new Map<number, Contact>();
  target = -1;
  route: { x: number; z: number }[] = [];
  private wp = 0;
  private poi: { x: number; z: number; until: number } | null = null;
  desiredHeading = 0;
  private decisionTimer = 0;
  private perceptionTimer = 0;
  private gunTimer = 0;
  /** A short turn to bring a loaded broadside to bear, overriding the plan. */
  private present: { heading: number; until: number } | null = null;
  private reactionTimer = 0;
  private readonly rng: Rng;
  /** Heard or seen things waiting for the reaction delay. */
  private pendingSignals: { t: number; c: Contact }[] = [];
  private signalled = new Set<number>();
  /** Explanation of the last decision (for the HUD / debugging). */
  intent = '';

  constructor(readonly vessel: Vessel, difficulty: DifficultyId, seed: number) {
    this.rng = new Rng(seed);
    this.personality = PERSONALITIES[vessel.cls.personality];
    this.difficulty = DIFFICULTIES[difficulty];
    this.desiredHeading = vessel.ship.heading;
  }

  // ------------------------------------------------------------------ perception

  private perceive(dt: number, bf: Battlefield): void {
    const me = this.vessel;
    const ship = me.ship;
    for (const o of bf.vessels) {
      if (o === me || !o.alive) continue;
      const dx = o.ship.com.x - ship.com.x, dz = o.ship.com.z - ship.com.z;
      const d = Math.hypot(dx, dz);
      const range = sightRange(me, o, bf);
      let c = this.contacts.get(o.id);
      if (!c) {
        c = { vessel: o.id, x: o.ship.com.x, z: o.ship.com.z, vx: 0, vz: 0, heading: 0, seen: -1e9, awareness: 0, visible: false, secondHand: false };
        this.contacts.set(o.id, c);
      }
      if (d < range) {
        const rate = awarenessRate(d, range, me.crew.gunnery);
        c.awareness = Math.min(1.5, c.awareness + rate * dt);
      } else {
        c.awareness = Math.max(0, c.awareness - dt * 0.05);
      }
      const nowVisible = c.awareness >= 1 && d < range * 1.1;
      if (nowVisible) {
        // Bearing is good; range is judged, with an error growing with distance.
        const err = 1 + this.rng.gauss() * 0.04 * (d / Math.max(range, 1));
        const ex = ship.com.x + dx * err, ez = ship.com.z + dz * err;
        const k = c.visible ? 0.25 : 1;
        if (c.visible) {
          c.vx += ((ex - c.x) / dt - c.vx) * 0.1;
          c.vz += ((ez - c.z) / dt - c.vz) * 0.1;
        } else {
          c.vx = o.ship.vel.x;
          c.vz = o.ship.vel.z;
          if (hostile(me, o)) bf.events.push({ type: 'sighted', vessel: me.id, target: o.id });
        }
        c.x += (ex - c.x) * k;
        c.z += (ez - c.z) * k;
        c.heading = o.ship.heading;
        c.seen = bf.time;
        c.secondHand = false;
      } else if (c.seen > -1e8) {
        // Lost: dead-reckon from the last estimate.
        c.x += c.vx * dt;
        c.z += c.vz * dt;
      }
      c.visible = nowVisible;
    }
    // Gunfire carries a long way; explosions and burning ships are seen.
    for (const e of bf.recent) {
      if (e.type !== 'cannonFire' && e.type !== 'explosion') continue;
      if (e.type === 'cannonFire' && e.vessel === me.id) continue;
      const d = Math.hypot(e.x - ship.com.x, e.z - ship.com.z);
      const heard = e.type === 'cannonFire' ? d < 12000 : d < bf.visibility * 1.5;
      if (!heard || (this.poi && this.poi.until > bf.time + 60)) continue;
      const brg = Math.atan2(e.z - ship.com.z, e.x - ship.com.x) + this.rng.gauss() * 0.12;
      const est = d * (1 + this.rng.gauss() * 0.3);
      this.poi = { x: ship.com.x + Math.cos(brg) * est, z: ship.com.z + Math.sin(brg) * est, until: bf.time + 240 };
    }
  }

  /** Pass our sightings to consorts in signalling range. */
  private signal(bf: Battlefield): void {
    const me = this.vessel;
    const day = bf.daylight > 0.4;
    const reach = day ? Math.min(bf.visibility, 5000) : 7000;
    for (const c of this.contacts.values()) {
      if (!c.visible || c.secondHand) continue;
      const target = bf.vessels.find((v) => v.id === c.vessel);
      if (!target || !hostile(me, target) || this.signalled.has(c.vessel)) continue;
      this.signalled.add(c.vessel);
      if (this.difficulty.coordination <= 0) continue;
      bf.events.push({ type: 'signal', vessel: me.id, x: me.ship.com.x, y: 30, z: me.ship.com.z });
      for (const ally of bf.vessels) {
        if (ally === me || !ally.alive || ally.faction !== me.faction || !ally.captain) continue;
        if (Math.hypot(ally.ship.com.x - me.ship.com.x, ally.ship.com.z - me.ship.com.z) > reach) continue;
        ally.captain.receive({ ...c, secondHand: true }, this.difficulty.signalDelay * this.rng.range(0.7, 1.4));
      }
    }
  }

  receive(c: Contact, delay: number): void {
    this.pendingSignals.push({ t: delay, c });
  }

  // ------------------------------------------------------------------ decisions

  update(dt: number, bf: Battlefield): number {
    const me = this.vessel;
    const ship = me.ship;
    this.modeTime += dt;
    if (!me.fighting) {
      this.intent = me.state === 'struck' ? 'Colours struck' : me.state === 'sinking' ? 'Abandoning ship' : '';
      return 0;
    }
    this.perceptionTimer -= dt;
    if (this.perceptionTimer <= 0) {
      this.perceptionTimer = 0.5;
      this.perceive(0.5, bf);
      this.signal(bf);
    }
    for (let i = this.pendingSignals.length - 1; i >= 0; i--) {
      const p = this.pendingSignals[i]!;
      p.t -= dt;
      if (p.t > 0) continue;
      this.pendingSignals.splice(i, 1);
      const mine = this.contacts.get(p.c.vessel);
      if (!mine || (!mine.visible && mine.seen < p.c.seen)) this.contacts.set(p.c.vessel, { ...p.c, awareness: mine?.awareness ?? 0 });
    }
    this.decisionTimer -= dt;
    this.reactionTimer -= dt;
    if (this.decisionTimer <= 0) {
      this.decisionTimer = this.difficulty.decisionInterval * this.rng.range(0.8, 1.2);
      this.decide(bf);
    }
    this.gunTimer -= dt;
    if (this.gunTimer <= 0) {
      this.gunTimer = 0.25;
      this.fightGuns(bf);
    }
    // Helm: steer for the chosen heading, easing off as the swing builds.
    if (this.present && bf.time > this.present.until) this.present = null;
    const err = wrapAngle((this.present?.heading ?? this.desiredHeading) - ship.heading);
    const rate = -ship.angVel.y;
    return clamp(err * 3.2 - rate * 5, -1, 1);
  }

  private knownTarget(bf: Battlefield): { v: Vessel; c: Contact } | null {
    const me = this.vessel;
    let best: { v: Vessel; c: Contact } | null = null;
    let bs = -Infinity;
    for (const c of this.contacts.values()) {
      if (bf.time - c.seen > 120) continue;
      const v = bf.vessels.find((o) => o.id === c.vessel);
      if (!v || !v.alive || !hostile(me, v) || !v.fighting) continue;
      const d = Math.hypot(c.x - me.ship.com.x, c.z - me.ship.com.z);
      // Prefer the nearest; good captains pick on the most battered.
      let s = -d / 1000 + (c.visible ? 1 : 0) + (v.faction === 'player' ? 0.5 : 0);
      s += (1 - v.damage.integrity) * 2 * this.difficulty.tactics;
      if (c.vessel === this.target) s += 0.8;
      if (s > bs) {
        bs = s;
        best = { v, c };
      }
    }
    return best;
  }

  private decide(bf: Battlefield): void {
    const me = this.vessel;
    const ship = me.ship;
    const pers = this.personality;
    const known = this.knownTarget(bf);
    const wasTarget = this.target;
    this.target = known ? known.v.id : -1;
    if (this.target !== wasTarget && this.target >= 0) this.reactionTimer = this.difficulty.reaction * this.rng.range(0.7, 1.3);
    // Reaction delay: keep doing what we were doing until it sinks in.
    if (this.reactionTimer > 0 && known && this.mode !== 'engage') return;

    const hull = me.damage.integrity;
    const crew = me.crew.strength;
    const mobility = ship.sails.sailHealth * ship.rudderHealth;
    let next: Mode = this.mode;
    if (known) {
      const { v, c } = known;
      const d = Math.hypot(c.x - ship.com.x, c.z - ship.com.z);
      const ratio = power(me) / Math.max(0.1, power(v) * this.rng.range(0.85, 1.15));
      const hurt = hull < pers.retreatHull || crew < pers.retreatCrew || ship.flooding > 0.5;
      const outgunned = (pers.runsFromStronger && ratio < 1.5) || ratio < 0.3 * (1 + pers.aggression);
      const theirMobility = v.ship.sails.sailHealth * v.ship.rudderHealth;
      if (me.faction === 'merchant') next = 'flee';
      else if (hurt || outgunned) next = this.allyNear(bf) && this.difficulty.coordination > 0.3 ? 'regroup' : 'flee';
      else if (pers.boarding > 0.4 && d < 700 && (me.crew.effective > v.crew.effective * (1.6 - pers.boarding * 0.5) || theirMobility < 0.35)) next = 'board';
      else if (pers.ramming > 0.3 && d < 450 && ship.mass > v.ship.mass * 1.2 && theirMobility < 0.5) next = 'ram';
      else if (d > this.engageRange() * 1.3) next = 'pursue';
      else next = 'engage';
      // Stand by a battered consort.
      const hurtAlly = this.hurtAlly(bf);
      if (hurtAlly && next === 'engage' && pers.aggression < 0.8 && this.difficulty.coordination > 0.4) next = 'regroup';
      me.battle = d < 3200;
      if (mobility < 0.15 && next !== 'board') next = 'engage';
    } else if (this.poi && this.poi.until > bf.time) {
      next = Math.hypot(this.poi.x - ship.com.x, this.poi.z - ship.com.z) < 400 ? 'search' : 'investigate';
      me.battle = false;
    } else {
      next = 'patrol';
      me.battle = false;
    }
    if (next !== this.mode) {
      this.mode = next;
      this.modeTime = 0;
    }
    this.chooseHeading(bf, known);
    this.chooseSail(bf, known);
    if (known) {
      const rel = wrapAngle(Math.atan2(known.c.z - ship.com.z, known.c.x - ship.com.x) - Math.atan2(bearingToZ(ship.heading), bearingToX(ship.heading)));
      // Enemy on our starboard side when rel is positive (clockwise from bow).
      me.threatSide = rel > 0 ? 1 : -1;
      me.threatRange = Math.hypot(known.c.x - ship.com.x, known.c.z - ship.com.z);
    } else {
      me.threatSide = 0;
      me.threatRange = Infinity;
    }
  }

  private engageRange(): number {
    return Math.min(AMMO[this.vessel.battery.ammo].range * 0.8, this.personality.range * 1.8 + 150);
  }

  private allyNear(bf: Battlefield): Vessel | null {
    const me = this.vessel;
    let best: Vessel | null = null, bd = 6000;
    for (const v of bf.vessels) {
      if (v === me || !v.alive || v.faction !== me.faction || !v.fighting) continue;
      const d = Math.hypot(v.ship.com.x - me.ship.com.x, v.ship.com.z - me.ship.com.z);
      if (d < bd) { bd = d; best = v; }
    }
    return best;
  }

  private hurtAlly(bf: Battlefield): Vessel | null {
    const me = this.vessel;
    for (const v of bf.vessels) {
      if (v === me || !v.alive || v.faction !== me.faction || !v.fighting || !v.battle) continue;
      if (v.damage.integrity < 0.45 && Math.hypot(v.ship.com.x - me.ship.com.x, v.ship.com.z - me.ship.com.z) < 2500) return v;
    }
    return null;
  }

  /** Score candidate headings over a short look-ahead and pick the best. */
  private chooseHeading(bf: Battlefield, known: { v: Vessel; c: Contact } | null): void {
    const me = this.vessel;
    const ship = me.ship;
    const pers = this.personality;
    const diff = this.difficulty;
    const windFrom = bf.wind.fromBearing;
    const T = diff.horizon;
    const vBest = Math.max(2, 4.8 * Math.sqrt(ship.sx) * (0.4 + 0.6 * ship.sails.sailHealth));
    const px = ship.com.x, pz = ship.com.z;
    const tgt = known?.c;
    const tv = known?.v;
    const tx = tgt ? tgt.x + tgt.vx * T : 0, tz = tgt ? tgt.z + tgt.vz * T : 0;
    // Flanking slot around the target, shared with consorts.
    let slot: { x: number; z: number } | null = null;
    if (tgt && tv && diff.coordination > 0.2 && (this.mode === 'engage' || this.mode === 'pursue')) {
      const list = bf.attackers.get(tv.id) ?? [];
      const k = list.indexOf(me.id);
      if (list.length > 1 && k >= 0) {
        const slots = [2.1, -2.1, 1.0, -1.0, Math.PI];
        const a = Math.atan2(bearingToZ(tgt.heading), bearingToX(tgt.heading)) + slots[k % slots.length]!;
        slot = { x: tx + Math.cos(a) * pers.range, z: tz + Math.sin(a) * pers.range };
      }
    }
    let intercept: { x: number; z: number } | null = null;
    if (tgt && (this.mode === 'pursue' || this.mode === 'ram' || this.mode === 'board')) {
      // Where can we meet her, given our speed and her course?
      let t = Math.hypot(tgt.x - px, tgt.z - pz) / vBest;
      for (let i = 0; i < 4; i++) t = Math.hypot(tgt.x + tgt.vx * t - px, tgt.z + tgt.vz * t - pz) / vBest;
      intercept = { x: tgt.x + tgt.vx * t, z: tgt.z + tgt.vz * t };
    }
    const ally = this.mode === 'regroup' ? this.allyNear(bf) ?? this.hurtAlly(bf) : null;

    let best = ship.heading, bs = -Infinity;
    const n = diff.headings;
    for (let i = 0; i < n; i++) {
      const h = (i / n) * Math.PI * 2 + this.rng.range(-0.05, 0.05);
      const windAngle = wrapAngle(windFrom - h);
      const v = vBest * polar(windAngle);
      const fx = bearingToX(h), fz = bearingToZ(h);
      // The first few seconds she carries on as she was.
      const qx = px + ship.vel.x * 4 + fx * v * (T - 4);
      const qz = pz + ship.vel.z * 4 + fz * v * (T - 4);
      let s = 0;
      if (Math.abs(windAngle) < 0.95) s -= 60;
      s -= Math.abs(wrapAngle(h - ship.heading)) * 0.35;
      switch (this.mode) {
        case 'patrol': {
          const w = this.route[this.wp % Math.max(1, this.route.length)];
          if (w) {
            if (Math.hypot(w.x - px, w.z - pz) < 400) this.wp++;
            s -= Math.hypot(w.x - qx, w.z - qz) / 200;
          } else s -= Math.abs(wrapAngle(h - ship.heading)) * 2;
          break;
        }
        case 'investigate':
        case 'search': {
          const p = this.poi!;
          const jitter = this.mode === 'search' ? 250 : 0;
          s -= Math.hypot(p.x + Math.cos(this.modeTime * 0.05) * jitter - qx, p.z + Math.sin(this.modeTime * 0.05) * jitter - qz) / 200;
          break;
        }
        case 'engage': {
          if (!tgt || !tv) break;
          const d = Math.hypot(tx - qx, tz - qz);
          s -= Math.pow((d - pers.range) / pers.range, 2) * 6;
          // Guns bear on the beam: we want her abeam of us.
          const rel = wrapAngle(Math.atan2(tz - qz, tx - qx) - Math.atan2(fz, fx));
          const off = Math.abs(Math.abs(rel) - Math.PI / 2) / (Math.PI / 2);
          const side = rel > 0 ? 1 : -1;
          const loaded = me.battery.ready(side as 1 | -1) / Math.max(1, me.battery.count(side as 1 | -1));
          s += (1 - off * off) * (4 + loaded * 3);
          // With guns loaded and in range, bring them to bear now: turn
          // her across the line of fire rather than wait for the geometry.
          const relNow = wrapAngle(Math.atan2(tgt.z - pz, tgt.x - px) - Math.atan2(fz, fx));
          const offNow = Math.abs(Math.abs(relNow) - Math.PI / 2) / (Math.PI / 2);
          const sideNow = (relNow > 0 ? 1 : -1) as 1 | -1;
          const readyNow = me.battery.ready(sideNow) / Math.max(1, me.battery.count(sideNow));
          const dNow = Math.hypot(tgt.x - px, tgt.z - pz);
          if (dNow < AMMO[me.battery.ammo].range) s += (1 - offNow * offNow) * readyNow * 7 * (0.5 + 0.5 * diff.tactics);
          // Stay out of her broadside, and cross her bow or stern to rake.
          const relT = wrapAngle(Math.atan2(qz - tz, qx - tx) - Math.atan2(bearingToZ(tgt.heading), bearingToX(tgt.heading)));
          const inHerArc = 1 - Math.abs(Math.abs(relT) - Math.PI / 2) / (Math.PI / 2);
          const theirGuns = tv.battery.ready(relT > 0 ? 1 : -1) / Math.max(1, tv.battery.count(relT > 0 ? 1 : -1));
          s -= inHerArc * inHerArc * theirGuns * 5 * (1 - pers.aggression * 0.6) * (0.4 + diff.tactics);
          const rake = Math.max(0, Math.cos(relT) * Math.cos(relT) - 0.7) * 3;
          s += rake * 6 * diff.tactics * (d < pers.range * 1.5 ? 1 : 0.3);
          // Weather gauge: upwind of her gives us the choice of range.
          const upwind = (qx - tx) * -bearingToX(windFrom) + (qz - tz) * -bearingToZ(windFrom);
          s += clamp(-upwind / 400, -1, 1) * 0.8 * pers.aggression * diff.tactics;
          if (slot) s -= (Math.hypot(slot.x - qx, slot.z - qz) / pers.range) * 3 * diff.coordination;
          if (d < 60) s -= 20;
          break;
        }
        case 'pursue':
        case 'ram':
        case 'board': {
          if (!intercept || !tgt) break;
          const aim = this.mode === 'board' && tv
            ? { x: tgt.x + tgt.vx * 6 - bearingToX(tgt.heading) * 25, z: tgt.z + tgt.vz * 6 - bearingToZ(tgt.heading) * 25 }
            : intercept;
          s -= Math.hypot(aim.x - qx, aim.z - qz) / 60;
          if (this.mode === 'board' && tv) {
            // Come in on her quarter, not into her broadside.
            const relT = wrapAngle(Math.atan2(qz - tgt.z, qx - tgt.x) - Math.atan2(bearingToZ(tgt.heading), bearingToX(tgt.heading)));
            s -= (1 - Math.abs(Math.abs(relT) - Math.PI / 2) / (Math.PI / 2)) * 2;
          }
          if (slot) s -= (Math.hypot(slot.x - qx, slot.z - qz) / 800) * diff.coordination;
          break;
        }
        case 'flee': {
          if (tgt) {
            const d = Math.hypot(tgt.x + tgt.vx * T - qx, tgt.z + tgt.vz * T - qz);
            s += d / 150 + v * 2;
            // Don't run across her broadside.
            const relT = wrapAngle(Math.atan2(qz - tgt.z, qx - tgt.x) - Math.atan2(bearingToZ(tgt.heading), bearingToX(tgt.heading)));
            s -= (1 - Math.abs(Math.abs(relT) - Math.PI / 2) / (Math.PI / 2)) * 1.5;
          } else s += v * 3;
          const a = this.allyNear(bf);
          if (a) s -= Math.hypot(a.ship.com.x - qx, a.ship.com.z - qz) / 900;
          break;
        }
        case 'regroup': {
          if (ally) {
            // Between the hurt consort and the enemy, or alongside a friend.
            const gx = tgt ? (ally.ship.com.x * 2 + tgt.x) / 3 : ally.ship.com.x;
            const gz = tgt ? (ally.ship.com.z * 2 + tgt.z) / 3 : ally.ship.com.z;
            s -= Math.hypot(gx - qx, gz - qz) / 150;
          }
          break;
        }
        default: break;
      }
      // Keep off other ships (unless we mean to be alongside).
      for (const o of bf.vessels) {
        if (o === me || !o.alive) continue;
        if ((this.mode === 'ram' || this.mode === 'board') && o.id === this.target) continue;
        const ox = o.ship.com.x + o.ship.vel.x * T, oz = o.ship.com.z + o.ship.vel.z * T;
        const d = Math.hypot(ox - qx, oz - qz);
        if (d < 90) s -= (90 - d) / 12;
      }
      s += this.rng.gauss() * diff.noise * 2;
      if (s > bs) {
        bs = s;
        best = h;
      }
    }
    this.desiredHeading = best;
    this.intent = this.describe(known);
  }

  private describe(known: { v: Vessel; c: Contact } | null): string {
    const name = known ? known.v.name : '';
    switch (this.mode) {
      case 'patrol': return 'Patrolling';
      case 'investigate': return 'Investigating gunfire';
      case 'search': return 'Searching';
      case 'engage': return `Engaging ${name}`;
      case 'pursue': return `Chasing ${name}`;
      case 'flee': return 'Running';
      case 'regroup': return 'Closing on consort';
      case 'board': return `Closing to board ${name}`;
      case 'ram': return `Bearing down to ram ${name}`;
      default: return '';
    }
  }

  private chooseSail(bf: Battlefield, known: { v: Vessel; c: Contact } | null): void {
    const me = this.vessel;
    const rig = me.ship.sails;
    const wind = bf.wind.speed;
    let plan = 3;
    switch (this.mode) {
      case 'engage': plan = 2; break;
      case 'pursue': case 'flee': case 'ram': plan = 4; break;
      case 'board': {
        const d = known ? Math.hypot(known.c.x - me.ship.com.x, known.c.z - me.ship.com.z) : 999;
        plan = d < 120 ? 1 : d < 400 ? 2 : 4;
        break;
      }
      case 'regroup': plan = 3; break;
      default: plan = 3;
    }
    // Shorten sail for the weather, and a prudent captain more so.
    if (wind > 17) plan = Math.min(plan, 1);
    else if (wind > 13) plan = Math.min(plan, 2);
    if (plan !== rig.plan) rig.setPlan(plan);
  }

  // ------------------------------------------------------------------ gunnery

  private fightGuns(bf: Battlefield): void {
    const me = this.vessel;
    const ship = me.ship;
    const battery = me.battery;
    const tgtId = this.target;
    if (tgtId < 0) {
      me.gunSide = 0;
      return;
    }
    const c = this.contacts.get(tgtId);
    const tv = bf.vessels.find((v) => v.id === tgtId);
    if (!c || !tv || !tv.fighting || bf.time - c.seen > 6) return;
    const d = Math.hypot(c.x - ship.com.x, c.z - ship.com.z);

    // Choose what to load for the next rounds.
    this.chooseAmmo(d, tv);
    const ammo = AMMO[battery.ammo];
    const local = ship.worldToLocal(tmp.set(c.x, tv.ship.com.y, c.z), tmp);
    const side = (local.z > 0 ? 1 : -1) as 1 | -1;
    me.gunSide = side;
    if (d > ammo.range * (0.85 + 0.25 * this.personality.aggression)) return;
    // Is she on the beam, within the guns' arc?
    // The broadside cycle: with a side loaded and in range, put the helm over
    // to lay her on that beam; once it's fired she manoeuvres while reloading.
    if (this.mode === 'engage' || this.mode === 'pursue' || this.mode === 'regroup' || this.mode === 'flee') {
      const bearing = Math.atan2(c.x - ship.com.x, -(c.z - ship.com.z));
      let bestH = 0, bestS = -Infinity;
      for (const s of [1, -1] as const) {
        const frac = battery.ready(s) / Math.max(1, battery.count(s));
        if (frac < 0.6) continue;
        const h = wrapAngle(bearing - s * Math.PI / 2);
        const dh = wrapAngle(h - ship.heading);
        const turn = Math.abs(dh);
        // A short swing only (running ships even less), and never one that
        // would take her through the eye of the wind.
        if (turn > ((this.mode === 'flee' || this.mode === 'regroup') ? 0.5 : 1.3)) continue;
        let crosses = false;
        for (let k = 0; k <= 8 && !crosses; k++) crosses = Math.abs(wrapAngle(bf.wind.fromBearing - (ship.heading + (dh * k) / 8))) < 0.95;
        if (crosses) continue;
        const score = frac * 2 - turn;
        if (score > bestS) {
          bestS = score;
          bestH = h;
        }
      }
      if (bestS > -5 && d < ammo.range * (0.9 + 0.3 * this.personality.aggression)) this.present = { heading: bestH, until: bf.time + 1 };
    }
    // Range the guns: time of flight with drag, the fall of shot, and lead.
    const k = ammo.drag / Math.cbrt(battery.weight);
    const v0 = ammo.speed;
    let range = d;
    let tof = (Math.exp(k * range) - 1) / (k * v0);
    const leadX = c.vx - ship.vel.x, leadZ = c.vz - ship.vel.z;
    range = Math.hypot(c.x + leadX * tof - ship.com.x, c.z + leadZ * tof - ship.com.z);
    tof = (Math.exp(k * range) - 1) / (k * v0);
    // Train the guns on where she'll be when the shot arrives; if that's
    // beyond the handspikes' reach, hold fire and let the helm bring them round.
    const aimAt = ship.worldToLocal(tmp.set(c.x + leadX * tof, tv.ship.com.y, c.z + leadZ * tof), tmp);
    const off = Math.atan2(aimAt.x, Math.abs(aimAt.z));
    if (Math.abs(off) > TRAIN_LIMIT + 0.02 || aimAt.z * side <= 0) return;
    battery.setTrain(side, off + this.rng.gauss() * (0.004 + 0.012 * (1 - me.crew.gunnery)));
    const drop = 0.5 * 9.81 * tof * tof;
    // Aim: the waterline to sink her, her rigging to cripple her, the deck to clear it.
    const aimH = ammo.id === 'chain' ? 14 * tv.ship.sy : ammo.id === 'grape' ? 3 * tv.ship.sy : ammo.id === 'shell' ? 2 * tv.ship.sy : 0.6;
    const muzzleH = cannonMount(battery.guns[0]!.spec).y * ship.sy;
    const elev = Math.atan2(drop + (aimH - muzzleH) * 1, range);
    // The quoin compensates the present roll; the rest is timing and skill.
    const rollErr = this.rng.gauss() * (0.012 * (1 - me.crew.gunnery) + 0.004);
    battery.setElevation(side, elev + ship.heel * side * 0.9 + rollErr);
    // Fire on the roll's steady moment.
    const rollRate = Math.abs(ship.angVelLocal.x);
    const patience = 0.02 + (1 - me.crew.gunnery) * 0.08;
    if (rollRate > patience && this.rng.chance(0.7)) return;
    const loaded = battery.ready(side), total = battery.count(side);
    if (loaded === 0) return;
    if (this.rng.chance(this.personality.broadsides)) {
      if (loaded >= total * 0.7 || (loaded > 0 && d < 150)) {
        battery.fire(side);
        this.present = null;
      }
    } else {
      battery.guns.forEach((g, i) => {
        if (g.spec.side === side && g.loaded && this.rng.chance(0.5)) battery.fireGun(i);
      });
    }
  }

  private chooseAmmo(d: number, tv: Vessel): void {
    const me = this.vessel;
    const carried = me.cls.ammo;
    const pers = this.personality;
    let want: AmmoId = 'round';
    const theirRig = tv.ship.sails.sailHealth;
    if (carried.includes('grape') && d < 160 && (this.mode === 'board' || pers.aggression > 0.6)) want = 'grape';
    else if (carried.includes('chain') && d < 480 && theirRig > 0.4 && (pers.cripple || (this.mode === 'pursue' && this.difficulty.tactics > 0.4))) want = 'chain';
    else if (carried.includes('shell') && d < 900 && d > 250 && this.difficulty.tactics > 0.6 && tv.ship.sx > 0.9 && this.rng.chance(0.3)) want = 'shell';
    me.battery.ammo = want;
  }
}

const tmp = new Vec3();
